// 원장 호출 — 실시간 상태 훅 (Firestore 구독 2개 + 서버 시각 보정 + 연결 상태). 계약 docs/계약_원장호출_서버_2026-10-08.md §4-2·§4-3
//  - enabled=false(플래그 OFF·치과 아님)면 구독·callable 0
//  - 경과 시간은 서버 시각 기준: getDoctorCallClock {nowMs} → offset = nowMs − (t0+t1)/2. 진입 시 1회 + 10분마다. 실패 시 offset 0(이 기기 시계) + 콘솔 기록
//  - 연결 끊김 = navigator offline 또는 스냅샷 fromCache 또는 구독 오류 → 화면이 호출 버튼 비활성
//  - 응답 시간(ackMin>0)이 0이 되면 열린 화면 중 하나가 ④ 만료를 보낸다. rules 가 서버 시각으로 검사해 일찍이면 거부 → 1.5초 뒤 1회 재시도.
//    여러 탭이 동시에 보내 거부되는 것은 정상(계약: 무시하고 구독 결과를 따른다). 탭이 하나도 없으면 서버 스케줄이 대신 만료
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    subscribeClinic, subscribeCells, openCall, clearCall, expireCall, ackCall, deskAction as apiDeskAction, setPresent as apiSetPresent, getDoctorCallClock,
} from '../../../services/doctorCallApi';
import { DEFAULT_SETTINGS, toMs, ackRemainMs, doctorCallError } from '../../../utils/doctorCall';
import { timed } from '../../../utils/dcTiming';

const OFFSET_REFRESH_MS = 10 * 60 * 1000;

// 서버 시각 오프셋 — 페이지 전체에서 하나(카드·띠·전체 화면이 같은 값)
let sharedOffset = 0;
let offsetAt = 0;
let offsetBusy = false;
async function refreshOffset(force) {
    if (offsetBusy || (!force && offsetAt && Date.now() - offsetAt < OFFSET_REFRESH_MS - 1000)) return;
    offsetBusy = true;
    try {
        const t0 = Date.now();
        const r = await getDoctorCallClock();
        const t1 = Date.now();
        const srv = Number(r?.nowMs);
        if (!Number.isFinite(srv)) { console.error('[원장 호출] 서버 시각 응답 형식 오류 — 이 기기 시계로 표시'); return; }
        sharedOffset = srv - (t0 + t1) / 2;
        offsetAt = Date.now();
    } catch (e) {
        console.error('[원장 호출] 서버 시각 확인 실패 — 이 기기 시계로 표시:', e?.code || e?.message || e, e?.details?.code || '');
    } finally {
        offsetBusy = false;
    }
}

const isDenied = (e) => String(e?.code || '').includes('permission-denied');

// 즉시 반영(2026-10-08 대표 증상 "누를 때마다 느리다"): Firestore 는 내 쓰기를 서버 응답 전에 로컬 스냅샷으로 바로 내보낸다(hasPendingWrites).
//  그때 serverTimestamp 필드는 'estimate' = 이 기기 시계 값이라, 서버 시각 기준(nowMs = 기기 + offset)으로 보면 경과가 offset 만큼 어긋난다.
//  → 대기 중 문서의 방금 찍힌(10초 이내) 시각 필드만 offset 을 더해 서버 시각 기준으로 맞춘다 = 누르자마자 "대기 0:00". 서버 확정 값이 오면 그 값으로 바뀐다
const FRESH_MS = 10000;
function adjustPending(c) {
    if (!c?._pending) return c;
    const now = Date.now();
    const fix = (v) => {
        const ms = toMs(v);
        return Number.isFinite(ms) && Math.abs(now - ms) < FRESH_MS ? ms + sharedOffset : v;
    };
    return {
        ...c,
        createdAt: c.state === 'open' ? fix(c.createdAt) : c.createdAt,
        ack: c.ack ? { ...c.ack, at: fix(c.ack.at) } : c.ack,
        clearedAt: fix(c.clearedAt),
    };
}

export function useDoctorCall(clinicId, enabled) {
    const on = !!(enabled && clinicId);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [present, setPresentState] = useState(null);   // true 재실 / false 부재 / null 미설정
    const [calls, setCalls] = useState([]);
    const [loadedA, setLoadedA] = useState(false);
    const [loadedB, setLoadedB] = useState(false);
    const [err, setErr] = useState('');
    const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
    const [cacheA, setCacheA] = useState(true);
    const [cacheB, setCacheB] = useState(true);
    const [subErr, setSubErr] = useState('');
    const [tick, setTick] = useState(() => Date.now());
    // 누르는 동안 반투명·비활성(busyKey) 제거(10-08) — 연타 가드는 아래 inflightRef

    // 1초 틱 — 경과·남은 시간 표시
    useEffect(() => {
        if (!on) return undefined;
        const t = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(t);
    }, [on]);

    // 서버 시각 오프셋
    useEffect(() => {
        if (!on) return undefined;
        refreshOffset(false);
        const t = setInterval(() => refreshOffset(true), OFFSET_REFRESH_MS);
        return () => clearInterval(t);
    }, [on]);

    // 온라인/오프라인
    useEffect(() => {
        if (!on) return undefined;
        const up = () => setOnline(true);
        const down = () => setOnline(false);
        window.addEventListener('online', up);
        window.addEventListener('offline', down);
        return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
    }, [on]);

    // Firestore 구독 2개(치과 문서 + 칸 전체)
    useEffect(() => {
        if (!on) return undefined;
        let alive = true;
        setLoadedA(false); setLoadedB(false); setSubErr('');
        const fail = (label) => (e) => {
            console.error(`[원장 호출] ${label} 구독 실패:`, e?.code || e?.message || e);
            if (!alive) return;
            setSubErr(doctorCallError(e, '원장 호출 정보를 불러오지 못했습니다. 새로고침해 주세요.'));
            setLoadedA(true); setLoadedB(true);
        };
        const u1 = subscribeClinic(clinicId, (d, meta) => {
            if (!alive) return;
            setSettings(d.settings);
            setPresentState(d.present);
            setCacheA(!!meta?.fromCache);
            setLoadedA(true);
        }, fail('설정'));
        const u2 = subscribeCells(clinicId, (list, meta) => {
            if (!alive) return;
            setCalls(list.map(adjustPending));
            setCacheB(!!meta?.fromCache);
            setLoadedB(true);
        }, fail('호출'));
        return () => { alive = false; u1(); u2(); };
    }, [on, clinicId]);

    const loaded = loadedA && loadedB;
    const connected = on && online && !subErr && loaded && !cacheA && !cacheB;
    const nowMs = tick + sharedOffset;

    // ④ 응답 시간 지남 → 만료(연결돼 있을 때만). 거부되면 1.5초 뒤 1회 재시도, 그래도 거부면 다른 탭이 끝냈거나 서버 스케줄이 처리
    const expiringRef = useRef(new Set());
    useEffect(() => {
        if (!connected) return;
        calls.forEach(c => {
            if (c.state !== 'acked' || expiringRef.current.has(c.id)) return;
            const remain = ackRemainMs(c, nowMs);
            if (remain === null || remain > 0) return;
            expiringRef.current.add(c.id);
            expireCall(clinicId, c)
                .catch(e => new Promise((resolve) => setTimeout(resolve, 1500)).then(() => expireCall(clinicId, c)).catch(e2 => {
                    if (isDenied(e2)) console.warn('[원장 호출] 자동 해제 거부(이미 끝났거나 서버 시각 전) — 서버 정리에 맡김:', e2?.code);
                    else console.error('[원장 호출] 자동 해제 실패:', e2?.code || e2?.message || e2, '(첫 시도:', e?.code || e?.message || e, ')');
                }))
                .finally(() => { setTimeout(() => expiringRef.current.delete(c.id), 30000); });
        });
    }, [calls, nowMs, connected, clinicId]);

    // 최신 칸 목록 — 거부 뒤 "정말 실패인지" 구독 결과로 다시 보기 위해
    const callsRef = useRef(calls);
    callsRef.current = calls;
    const findCell = (cellId) => callsRef.current.find(c => c.cellId === cellId) || null;
    const isLiveCall = (c) => !!c && (c.state === 'open' || c.state === 'acked');
    const sameLiveCall = (call) => { const c = findCell(call.cellId); return isLiveCall(c) && c.id === call.id ? c : null; };
    const afterSettle = (fn) => setTimeout(fn, 1500);   // 거부 후 1.5초 뒤 구독 결과로 판정
    const logFail = (label, e) => console.error(`[원장 호출] ${label}:`, e?.code || e?.message || e, e?.details?.code || '');

    // 같은 버튼 연타 가드 — 서버 응답 전 같은 키 재입력은 화면 변화 없이 무시(반투명·비활성 표시 없음)
    const inflightRef = useRef(new Set());
    const guard = (key) => {
        if (inflightRef.current.has(key)) return false;
        inflightRef.current.add(key);
        return true;
    };
    const release = (key) => inflightRef.current.delete(key);

    // 격자 칸 누름 — 진행 중이면 ③ 해제, 아니면(지남·끝남·없음) ① 새 호출
    //  화면은 Firestore 로컬 반영으로 누르는 즉시 바뀐다(서버 응답을 기다리지 않음). 응답 대기 중 같은 칸 연타는 무시
    //  거부(permission-denied)는 두 경우가 섞인다: (a) 다른 태블릿이 먼저 같은 칸을 바꿈(정상) (b) 2초 재열기 제한·플래그 OFF·rules 미배포 등 진짜 실패.
    //  그래서 거부 뒤 1.5초 구독 결과를 보고, 원하던 상태가 아니면 실패 문구를 띄운다(조용한 실패 금지). 거부되면 로컬 반영은 Firestore 가 되돌린다
    const toggleCell = useCallback(async (chairNo, situation, hit) => {
        if (!connected) return null;
        const key = `${chairNo}_${situation}`;
        if (!guard(`cell_${key}`)) return null;
        setErr('');
        const wantLive = hit?.kind !== 'live';
        try {
            if (wantLive) await timed('doctorCall', '호출', () => openCall(clinicId, chairNo, situation));
            else await timed('doctorCall', '끄기', () => clearCall(clinicId, hit.call));
            return true;
        } catch (e) {
            if (!isDenied(e)) {
                logFail(wantLive ? '호출 실패' : '호출 끄기 실패', e);
                setErr(doctorCallError(e, wantLive ? '호출하지 못했습니다. 다시 눌러 주세요.' : '호출을 끄지 못했습니다. 다시 눌러 주세요.'));
                return null;
            }
            console.warn(`[원장 호출] ${wantLive ? '호출' : '끄기'} 거부 — 1.5초 뒤 실제 상태 확인:`, e?.code);
            afterSettle(() => {
                const live = isLiveCall(findCell(key));
                if (wantLive && !live) {
                    logFail('호출 거부 후에도 칸이 꺼져 있음', e);
                    setErr('호출하지 못했습니다. 2초쯤 뒤 다시 눌러 주세요. 계속 안 되면 기능이 꺼져 있거나 서버 준비 전입니다.');
                } else if (!wantLive && live && findCell(key)?.id === hit.call.id) {
                    logFail('끄기 거부 후에도 같은 호출이 켜져 있음', e);
                    setErr('호출을 끄지 못했습니다. 다시 눌러 주세요.');
                }
            });
            return null;
        } finally { release(`cell_${key}`); }
    }, [connected, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 원장 응답 — 즉시 반영. 거부됐는데 그 호출이 이미 끝났으면 "이미 끝난 호출입니다"
    const ack = useCallback(async (call, type) => {
        if (!connected) return null;
        const gk = `ack_${call.cellId}`;
        if (!guard(gk)) return null;
        setErr('');
        try { await timed('doctorCall', '응답', () => ackCall(clinicId, call, type, settings.ackSet)); return true; }
        catch (e) {
            if (isDenied(e)) {
                afterSettle(() => {
                    if (!sameLiveCall(call)) { console.warn('[원장 호출] 응답 거부 — 이미 끝난 호출:', e?.code); setErr('이미 끝난 호출입니다.'); }
                    else { logFail('응답 거부', e); setErr(doctorCallError(e, '응답을 보내지 못했습니다. 다시 눌러 주세요.')); }
                });
                return null;
            }
            logFail('응답 실패', e);
            setErr(doctorCallError(e, '응답을 보내지 못했습니다. 다시 눌러 주세요.'));
            return null;
        } finally { release(gk); }
    }, [connected, clinicId, settings.ackSet]); // eslint-disable-line react-hooks/exhaustive-deps

    const setPresent = useCallback(async (v) => {
        if (!connected || !guard('presence')) return null;
        setErr('');
        try { await timed('doctorCall', '재실/부재', () => apiSetPresent(clinicId, v)); return true; }
        catch (e) { logFail('재실/부재 변경 실패', e); setErr(doctorCallError(e, '재실/부재를 바꾸지 못했습니다. 다시 눌러 주세요.')); return null; }
        finally { release('presence'); }
    }, [connected, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 데스크 기록 — 대상 호출 여러 건, 필드가 비어 있는 것만(서버: null일 때만 1회 쓰기). 즉시 반영("전화함 ✓"가 바로 보임)
    //  거부된 건: 이미 기록됨 → 그대로 "기록됨" 표시(성공 취급) / 호출이 끝남 → "이미 끝난 호출입니다" / 그 외 → 오류
    const deskAct = useCallback(async (targetCalls, type) => {
        const field = type === 'called' ? 'deskCalledAt' : 'deskVisitorAt';
        const targets = (targetCalls || []).filter(c => !c[field]);
        if (!connected || !targets.length) return null;
        const gk = `desk_${type}`;
        if (!guard(gk)) return null;
        setErr('');
        try {
            const rs = await timed('doctorCall', type === 'called' ? '데스크 전화함' : '데스크 방문객', () => Promise.allSettled(targets.map(c => apiDeskAction(clinicId, c, type))));
            const failed = rs.map((r, i) => (r.status === 'rejected' ? { call: targets[i], e: r.reason } : null)).filter(Boolean);
            if (!failed.length) return true;
            const hard = failed.filter(f => !isDenied(f.e));
            if (hard.length) {
                logFail('데스크 기록 실패', hard[0].e);
                setErr(doctorCallError(hard[0].e, '데스크 기록을 남기지 못했습니다. 다시 눌러 주세요.'));
                return null;
            }
            afterSettle(() => {
                let ended = 0, other = null;
                failed.forEach(f => {
                    const c = findCell(f.call.cellId);
                    if (!c || c.id !== f.call.id || !isLiveCall(c)) ended += 1;
                    else if (!c[field]) other = f.e;   // 기록도 안 됐고 호출도 살아 있음 = 진짜 실패
                });
                if (other) { logFail('데스크 기록 거부', other); setErr(doctorCallError(other, '데스크 기록을 남기지 못했습니다. 다시 눌러 주세요.')); }
                else if (ended) { console.warn('[원장 호출] 데스크 기록 거부 — 이미 끝난 호출', ended, '건'); setErr('이미 끝난 호출입니다.'); }
            });
            return null;
        } finally { release(gk); }
    }, [connected, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 진행 중(open/acked) 호출
    const live = useMemo(() => calls.filter(c => c.state === 'open' || c.state === 'acked'), [calls]);
    // 미응답이 deskShowSec 이상(데스크 띠·카드 깜빡임) — 오래된 순
    const deskOverdue = useMemo(() => calls.filter(c => {
        if (c.state !== 'open') return false;
        const w = nowMs - toMs(c.createdAt);
        return Number.isFinite(w) && w >= settings.deskShowSec * 1000;
    }).sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt)), [calls, nowMs, settings.deskShowSec]);

    return {
        on, loaded, err: err || subErr, connected, settings, present, calls, live, nowMs, deskOverdue,
        toggleCell, ack, setPresent, deskAct,
    };
}
